import {
  frand_rng,
  arr_pick,
  fib_num,
  render_dims,
  fx_shuffle_arr,
} from "./local_mods/util.js";

import { TWO_PI } from "./local_mods/math_constants.js";

// a circle with radius relative to magnitude
const button_mode = {
  title: "button",
  init_render: function (p55, scene, q) {
    const norm_sz_max = Math.pow(scene.i_OPR, 1);
    const norm_sz_min = Math.pow(scene.i_OPR, 7);
    const norm_sz = p55.map(q.mag, 0, scene.max_mag, norm_sz_min, norm_sz_max);

    const clrp_idx = Math.floor(
      p55.map(q.mag, 0, scene.max_mag, 1, scene.palette.length - 1)
    );
    const stroke_clr_idx = Math.floor(
      p55.map(q.mag, 0, scene.max_mag, scene.palette.length - 1, 1)
    );
    const ring_clr_idx = Math.floor(
      p55.map(q.mag, 0, scene.max_mag, scene.palette.length - 1, 1)
    );
    const c_fill = p55.color(scene.palette[clrp_idx]);
    const c_stroke = p55.color(scene.palette[stroke_clr_idx]);

    const render = function (q, curr_dims, pg) {
      const min_dim = Math.min(curr_dims.x, curr_dims.y); // use this to scale up the norm'd line lengths
      c_fill.setAlpha(scene.i_OPR * 255);
      pg.noStroke();
      pg.fill(c_fill);
      let sz = min_dim * norm_sz;
      pg.ellipse(q.loc.render.x, q.loc.render.y, sz, sz);
    };
    return render;
  },
};

// spokes of fine lines in segments
const morse_code_mode = {
  title: "morse_code",
  init_render: function (p55, scene, q) {
    const scale_fact = 521;
    const curr_q_depth = q.loc.lld.z > 0 ? q.loc.lld.z : 0; // check if depth is negative value and set it to 0
    const init_rot = (curr_q_depth / scene.max_depth) * TWO_PI;
    const norm_stroke_w = (1 - scene.i_OPR) / scale_fact;

    const spoke_num_min_fact = 3; // fib_num(1,3,3) = 18
    const spoke_num_max_fact = 11; // fib_num(1,3,11) = 843
    const spoke_num_fact_rng = spoke_num_max_fact - spoke_num_min_fact;
    const spoke_num_fact =
      Math.floor((curr_q_depth / scene.max_depth) * spoke_num_fact_rng) +
      spoke_num_min_fact;
    const num_spokes = fib_num(1, 3, spoke_num_fact);

    const norm_max_seg_len = 29 / scale_fact; // 0.05566
    const norm_space = {}; // spacing between segments of the line
    norm_space.min = 7 / scale_fact;
    norm_space.max = 29 / scale_fact;

    norm_space.actual = p55.map(
      curr_q_depth,
      0,
      scene.max_depth,
      norm_space.min,
      norm_space.max
    );

    const spokes = [];
    let norm_segs_lens = [];
    const num_segs_fact = Math.floor(p55.map(q.mag, 0, scene.max_mag, 0, 3));
    const num_segs = fib_num(1, 3, num_segs_fact);
    for (let sp = 0; sp < num_spokes; sp++) {
      norm_segs_lens = [];
      for (let i = 0; i < num_segs; i++) {
        const curr_len = frand_rng(norm_space.actual, norm_max_seg_len);
        norm_segs_lens[i] = curr_len;
      }
      spokes.push(norm_segs_lens);
    }

    const pal_idx = Math.floor(
      p55.map(curr_q_depth, 0, scene.max_depth, scene.palette.length - 1, 1)
    );

    const strk_clr = p55.color(scene.palette[pal_idx]);
    strk_clr.setAlpha(199);

    const render = function (q, curr_dims, pg) {
      // these lines can't be pre-calc'd bc they need to know the current dimensions
      const min_dim = Math.min(curr_dims.x, curr_dims.y); // use this to scale up the norm'd line lengths
      const stroke_w = norm_stroke_w * min_dim;
      const space_len = norm_space.actual * min_dim;

      pg.stroke(strk_clr);
      pg.noFill();
      pg.strokeWeight(stroke_w); // as defined by norm'd val and minimum dimension
      pg.push();
      pg.translate(q.loc.render.x, q.loc.render.y);

      pg.rotate(init_rot);
      const spoke_rot_incr = TWO_PI / spokes.length - 1;
      for (let sp = 0; sp < spokes.length; sp++) {
        pg.rotate(spoke_rot_incr);

        const curr_sp = spokes[sp];
        let curr_line_len = 0;
        for (let i = 0; i < curr_sp.length; i++) {
          const curr_line_seg_len = curr_sp[i] * min_dim;
          pg.line(curr_line_len, 0, curr_line_len + curr_line_seg_len, 0);
          curr_line_len = curr_line_len + curr_line_seg_len + space_len;
        }
      }
      pg.pop();
    };
    return render;
  },
};

// circles spaced out, radiating outwards from q loc
const radial_out_mode = {
  title: "radial_out",
  init_render: function (p55, scene, q) {
    const clrp_idx = Math.floor(
      p55.map(q.mag, 0, scene.max_mag, 0, scene.palette.length - 1)
    );
    const stroke_clr_idx = Math.floor(
      p55.map(q.mag, 0, scene.max_mag, scene.palette.length - 1, 0)
    );
    const c_stroke = p55.color(scene.palette[stroke_clr_idx]);

    const norm_strokeW = Math.pow(scene.i_OPR, 16);
    const norm_sz_max = scene.OPR;
    const norm_sz_min = scene.i_OPR;
    const norm_sz = p55.map(q.mag, 0, scene.max_mag, norm_sz_min, norm_sz_max);

    const render = function (q, curr_dims, pg) {
      const min_dim = Math.min(curr_dims.x, curr_dims.y); // use this to scale up the norm'd line lengths
      pg.stroke(c_stroke);
      pg.noFill();
      const num_circs = q.mag * 3;
      const strokeW = norm_strokeW * min_dim;
      for (let i = 0; i < num_circs; i++) {
        let sz = p55.map(i, 0, num_circs, 1, norm_sz * min_dim);
        pg.strokeWeight(strokeW);
        pg.ellipse(q.loc.render.x, q.loc.render.y, sz, sz);
      }
    };
    return render;
  },
};

// two lines for each lat and long line of the q loc
const ll_cross_mode = {
  title: "ll_cross",
  init_render: function (p55, scene, q) {
    const stroke_clr = p55.color(scene.palette[0]);

    const norm_stroke_w = Math.pow(scene.i_OPR, 13);
    const norm_alpha_max = 1;
    const norm_alpha_min = Math.pow(scene.i_OPR, 7);
    const norm_alpha = p55.map(
      q.mag,
      0,
      scene.max_mag,
      norm_alpha_min,
      norm_alpha_max
    );
    stroke_clr.setAlpha(255 * norm_alpha);

    const render = function (q, curr_dims, pg) {
      if (scene.OPR.nat < 2.5) {
        const min_dim = Math.min(curr_dims.x, curr_dims.y); // use this to scale up the norm'd line lengths
        const strokeW = norm_stroke_w * min_dim;

        pg.stroke(stroke_clr);
        pg.strokeWeight(strokeW);

        let lon_x = q.loc.render.x;
        let lat_y = q.loc.render.y;
        //longitude
        pg.line(lon_x, 0, lon_x, curr_dims.y);
        //latitude
        pg.line(0, lat_y, curr_dims.x, lat_y);
      }
    };
    return render;
  },
};

// multiple overlapping tranlucent arcs
// This is render mode is similar to morse_code_mode where it's a mess. I had an idea for what this render mode would do and when I coded it out it actually didn't quite do what I was hoping. Instead it does something less and at the same time redundant to what my intention was. Regardless, I like it so I'm not 'fixing' it.
const pieish_chart_mode = {
  title: "pieish",
  init_render: function (p55, scene) {
    const scale_fact = 690;
    const norm_min_rad = 47 / scale_fact;
    const norm_max_rad = 521 / scale_fact;
    const fill_clr = p55.color(scene.palette[0]);

    const slices = [];
    const num_slices = Math.floor(frand_rng(1, 7));
    fill_clr.setAlpha(11); // no norm needed (except Norm Macdonald!)
    const slice_max_len = (TWO_PI / num_slices) * scene.OPR; // i think these should really read arc_len_max/min
    const slice_min_len = TWO_PI * (1 - scene.i_OPR);
    for (let i = 0; i < num_slices; i++) {
      slices.push(frand_rng(slice_min_len, slice_max_len));
    }
    const start_r = frand_rng(TWO_PI);

    const render = function (q, curr_dims, pg) {
      const min_dim = Math.min(curr_dims.x, curr_dims.y); // use this to scale up the norm'd line lengths
      const min_rad = norm_min_rad * min_dim;
      const max_rad = norm_max_rad * min_dim;
      const lon_x = q.loc.render.x;
      const lat_y = q.loc.render.y;
      const radius = p55.map(q.mag, 0, scene.max_mag, min_rad, max_rad);
      slices.forEach((s) => {
        const end_r = (TWO_PI - start_r) * scene.i_OPR;
        pg.noStroke();
        pg.fill(fill_clr);
        pg.arc(lon_x, lat_y, radius, radius, start_r, end_r);
      });
    };
    return render;
  },
};

export {
  radial_out_mode,
  button_mode,
  ll_cross_mode,
  morse_code_mode,
  pieish_chart_mode,
};
